import React from 'react'
import { useTranslation } from 'react-i18next'
import Stack from '@mui/material/Stack'
import {
  DataGridPro,
  GridColDef,
  GridRowParams,
  GridValueGetterParams,
  getGridSingleSelectOperators,
  useGridApiRef
} from '@mui/x-data-grid-pro'

import { formatEnum } from '@cbgms/base/utils/helpers'
import { IVehicleType } from '@cbgms/api/modules/vehicles'
import { useFilter } from 'app/hooks/useFilter'
import { useArrowScrollHandler } from '@carsys/ui/src/helpers/keyboard/use-arrow-scroll-handler'
import { useOnEnterHandler } from '@carsys/ui/src/helpers/keyboard/use-on-enter-handler'
import { DataGridToolbarWithSearch } from '../../grid/GridToolBarWithSearch'
import { useDataGridLocaleText } from '@cbgms/components/list/use-data-grid-locale-text'
import {
  buildYearDisplayValue,
  buildYearRange,
  generateUniqueOptions,
  getRowByIndex,
  getVisibleRowCount,
  onKeyScroll,
  parseVehicleDate,
  sortFilterOptions
} from '../../grid/Helpers'
import { GridHeaderFilterSingleSelect } from '../../grid/GridHeaderFilterSingleSelect'
import { GridOperatorIncludes } from '../../grid/GridOperatorIncludes'
import { GridDateRangeIncludes, extraDateRangeFilterType } from '../../grid/GridDateRangeIncludes'
import { gridHeaderStyling } from '../../grid/Styling'
import { SortDirection } from '@cbgms/api'
import { TFunction } from 'i18next'

export interface ITypeSelectionProps {
  onTypeSelect: (type: IVehicleType) => void
  types: IVehicleType[]
}

const filterTranslatedEnumValues = (keyword: string, type: IVehicleType): boolean => {
  if (type.FuelTypes.some(fuel => formatEnum('EnumFuelType', fuel).toLowerCase().includes(keyword))) {
    return true
  }
  if (formatEnum('EnumBodyType', type.BodyType).toLowerCase().includes(keyword)) {
    return true
  }
  return false
}

const filterFormattedValues = (t: TFunction, keyword: string, type: IVehicleType): boolean => {
  return (
    powerBhpAndKwCombinedLabel(t, type).toLowerCase().includes(keyword) ||
    buildYearDisplayValue(t, type.GenericVehicle.MadeFrom, type.GenericVehicle.MadeUntil).toLowerCase().includes(keyword)
  )
}

const extraFilters =
  (t: TFunction) =>
  (keyword: string, type: IVehicleType): boolean => {
    return extraDateRangeFilterType(keyword, type) || filterTranslatedEnumValues(keyword, type) || filterFormattedValues(t, keyword, type)
  }

const remarksValue = (type: IVehicleType) => type.Remarks || type.GenericVehicle.Remarks
const powerKwLabel = (t: TFunction, type: IVehicleType) => `${type.PowerKw} ${t('vehicle.engine_performance_unit_kw')}`
const powerBhpLabel = (t: TFunction, type: IVehicleType) => `${type.GenericVehicle.PowerBHP} ${t('vehicle.engine_performance_unit_hp')}`

const powerBhpAndKwCombinedLabel = (t: TFunction, type: IVehicleType) => {
  if (!type.GenericVehicle.PowerBHP || type.GenericVehicle.PowerBHP === 0) {
    return powerKwLabel(t, type)
  }
  if (!type.PowerKw || type.PowerKw === 0) {
    return powerBhpLabel(t, type)
  }
  return `${powerBhpLabel(t, type)} / ${powerKwLabel(t, type)}`
}

export const VehicleTypeList: React.FC<ITypeSelectionProps> = ({ types, onTypeSelect }) => {
  const { t } = useTranslation()
  const localeText = useDataGridLocaleText()
  const apiRef = useGridApiRef()

  const [filteredTableData, setFilter, filter] = useFilter(types, extraFilters(t))
  const [rowCount, setRowCount] = React.useState(filteredTableData.length)
  const { currentFocus, hasRowFocus } = useArrowScrollHandler(rowCount, 1, (index: number) => onKeyScroll(apiRef, index))
  useOnEnterHandler(hasRowFocus, () => {
    const { model } = getRowByIndex(apiRef, currentFocus)
    onTypeSelect(model as IVehicleType)
  })

  React.useEffect(() => {
    apiRef.current.subscribeEvent('stateChange', () => {
      const count = getVisibleRowCount(apiRef.current.state)
      setRowCount(count)
    })
  }, [apiRef])

  const fuel = React.useMemo(() => {
    const fuelOptions = filteredTableData.flatMap(type =>
      type.FuelTypes.map(fuel => ({ label: formatEnum('EnumFuelType', fuel), value: fuel.toString() }))
    )
    const uniqueFuelOptions = generateUniqueOptions(fuelOptions)
    const sortedOptions = sortFilterOptions(uniqueFuelOptions)

    const fuelTypeValues = filteredTableData.map(type => ({
      label: type.FuelTypes.map(fuel => formatEnum('EnumFuelType', fuel))
        .join(', ')
        .toString(),
      value: type.FuelTypes.join(', ').toString()
    }))

    return { options: sortedOptions, valueOptions: fuelTypeValues }
  }, [filteredTableData, generateUniqueOptions, formatEnum])

  const type = React.useMemo(() => {
    const types = filteredTableData.map(type => ({ value: type.Type, label: type.Type }))
    const uniqueTypeOptions = generateUniqueOptions(types)
    const sortedOptions = sortFilterOptions(uniqueTypeOptions)
    return { options: sortedOptions }
  }, [filteredTableData, generateUniqueOptions])

  const bodyType = React.useMemo(() => {
    const types = filteredTableData.map(type => ({ value: type.BodyType, label: formatEnum('EnumBodyType', type.BodyType) }))
    const uniqueTypeOptions = generateUniqueOptions(types)
    const sortedOptions = sortFilterOptions(uniqueTypeOptions)
    return { options: sortedOptions }
  }, [filteredTableData, generateUniqueOptions])

  const remark = React.useMemo(() => {
    const remarkOptions = filteredTableData.map(type => ({ value: remarksValue(type), label: remarksValue(type) }))
    const uniqueRemarkOptions = generateUniqueOptions(remarkOptions)
    const sortedOptions = sortFilterOptions(uniqueRemarkOptions)

    return { options: sortedOptions }
  }, [filteredTableData, generateUniqueOptions, remarksValue])

  const powerKw = React.useMemo(() => {
    const powerKwOptions = filteredTableData.map(type => ({
      value: powerBhpAndKwCombinedLabel(t, type),
      label: powerBhpAndKwCombinedLabel(t, type)
    }))
    const uniquePowerKwOptions = generateUniqueOptions(powerKwOptions)

    const sortedOptions = sortFilterOptions(uniquePowerKwOptions)

    return { options: sortedOptions }
  }, [filteredTableData, generateUniqueOptions])

  const engineCode = React.useMemo(() => {
    const engineCodeOptions = filteredTableData
      .flatMap(type => (type.EngineCode || '').split(','))
      .map(engineCode => ({ value: engineCode, label: engineCode }))
    const uniqueEngineCodeOptions = generateUniqueOptions(engineCodeOptions)
    const sortedOptions = sortFilterOptions(uniqueEngineCodeOptions)

    return { options: sortedOptions }
  }, [filteredTableData, generateUniqueOptions])

  const capacityCC = React.useMemo(() => {
    const capacities = filteredTableData.map(type => ({ value: type.CapacityCC, label: type.CapacityCC }))
    const uniqueOptions = generateUniqueOptions(capacities)
    const sortedOptions = sortFilterOptions(uniqueOptions)
    return { options: sortedOptions }
  }, [filteredTableData, generateUniqueOptions])

  const cylinders = React.useMemo(() => {
    const cylinderOptions = filteredTableData.map(type => ({ value: type.Cylinders, label: type.Cylinders }))
    const uniqueOptions = generateUniqueOptions(cylinderOptions)
    const sortedOptions = sortFilterOptions(uniqueOptions)
    return { options: sortedOptions }
  }, [filteredTableData, generateUniqueOptions])

  const buildYears = React.useMemo(() => {
    const result = filteredTableData.reduce(
      (result, type) => {
        const madeFrom = parseVehicleDate(type.GenericVehicle.MadeFrom).year()
        const madeUnil = parseVehicleDate(type.GenericVehicle.MadeUntil).year()
        if (result.from > madeFrom) {
          result.from = madeFrom
        }
        if (result.until < madeUnil) {
          result.until = madeUnil
        }
        return result
      },
      { from: new Date().getFullYear(), until: 0 }
    )

    const buildYearRanges = buildYearRange(result.from, result.until)
    const uniqueOptions = generateUniqueOptions(buildYearRanges)
    const sortedOptions = sortFilterOptions(uniqueOptions, SortDirection.Descending)

    return { options: sortedOptions }
  }, [filteredTableData, generateUniqueOptions, buildYearRange, sortFilterOptions])

  const columns: GridColDef[] = [
    {
      field: 'Type',
      align: 'left',
      headerName: t('vehicle.type'),
      minWidth: 100,
      flex: 1,
      disableColumnMenu: true,
      filterable: !!type.options.length,
      valueOptions: type.options,
      type: 'singleSelect',
      filterOperators: getGridSingleSelectOperators()
        .filter(op => op.value === 'is')
        .map(operator => ({
          ...operator,
          InputComponent: GridHeaderFilterSingleSelect,
          InputComponentProps: { options: type.options }
        }))
    },
    {
      field: 'BodyType',
      align: 'left',
      headerName: t('vehicle.body_type'),
      minWidth: 100,
      flex: 1,
      disableColumnMenu: true,
      filterable: !!bodyType.options.length,
      valueOptions: bodyType.options,
      type: 'singleSelect',
      filterOperators: getGridSingleSelectOperators()
        .filter(op => op.value === 'is')
        .map(operator => ({
          ...operator,
          InputComponent: GridHeaderFilterSingleSelect,
          InputComponentProps: { options: bodyType.options }
        }))
    },
    {
      field: 'Remarks',
      align: 'right',
      headerName: t('vehicle.remarks'),
      valueGetter: (value: GridValueGetterParams<IVehicleType>) => remarksValue(value.row),
      minWidth: 50,
      flex: 1,
      disableColumnMenu: true,
      filterable: !!remark.options.length,
      valueOptions: remark.options,
      type: 'singleSelect',
      filterOperators: getGridSingleSelectOperators()
        .filter(op => op.value === 'is')
        .map(operator => ({
          ...operator,
          InputComponent: GridHeaderFilterSingleSelect,
          InputComponentProps: { options: remark.options }
        }))
    },
    {
      field: 'BuildYears',
      align: 'left',
      headerName: t('vehicle.construction_year_range'),
      minWidth: 200,
      flex: 1,
      disableColumnMenu: true,
      filterable: !!buildYears.options.length,
      valueGetter: (value: GridValueGetterParams<IVehicleType>) =>
        buildYearDisplayValue(t, value.row.GenericVehicle.MadeFrom, value.row.GenericVehicle.MadeUntil),
      filterOperators: GridDateRangeIncludes({ options: buildYears.options }, 'type')
    },
    {
      field: 'PowerKw',
      align: 'right',
      headerName: t('vehicle.engine_performance'),
      valueGetter: (value: GridValueGetterParams<IVehicleType>) => powerBhpAndKwCombinedLabel(t, value.row),
      minWidth: 175,
      flex: 1,
      disableColumnMenu: true,
      filterable: !!powerKw.options.length,
      filterOperators: getGridSingleSelectOperators()
        .filter(op => op.value === 'is')
        .map(operator => ({
          ...operator,
          getApplyFilterFn: filterItem => {
            return ({ value }): boolean => {
              if (value?.includes('/')) {
                return value.includes(filterItem.value)
              }
              return value === filterItem.value
            }
          },
          InputComponent: GridHeaderFilterSingleSelect,
          InputComponentProps: { options: powerKw.options }
        }))
    },
    {
      field: 'EngineCode',
      align: 'right',
      headerName: t('vehicle.engine_code'),
      minWidth: 100,
      flex: 1,
      disableColumnMenu: true,
      filterable: !!engineCode.options.length,
      type: 'singleSelect',
      filterOperators: getGridSingleSelectOperators()
        .filter(op => op.value === 'is')
        .map(operator => ({
          ...operator,
          getApplyFilterFn: filterItem => {
            return ({ value }): boolean => {
              if (value?.includes(',')) {
                return value.includes(filterItem.value)
              }
              return value === filterItem.value
            }
          },
          InputComponent: GridHeaderFilterSingleSelect,
          InputComponentProps: { options: engineCode.options }
        }))
    },

    {
      field: 'FuelTypes',
      align: 'right',
      headerName: t('vehicle.fuel'),
      minWidth: 150,
      flex: 1,
      disableColumnMenu: true,
      filterable: !!fuel.options.length,
      valueOptions: fuel.valueOptions,
      valueGetter: (value: GridValueGetterParams<IVehicleType>) => value.row.FuelTypes.join(', ').toString(),
      type: 'singleSelect',
      filterOperators: GridOperatorIncludes({ options: fuel.options })
    },
    {
      field: 'CapacityCC',
      align: 'right',
      headerName: t('vehicle.capacity_cc'),
      minWidth: 50,
      flex: 1,
      disableColumnMenu: true,
      filterable: !!capacityCC.options.length,
      valueOptions: capacityCC.options,
      type: 'singleSelect',
      filterOperators: getGridSingleSelectOperators()
        .filter(op => op.value === 'is')
        .map(operator => ({
          ...operator,
          InputComponent: GridHeaderFilterSingleSelect,
          InputComponentProps: { options: capacityCC.options }
        }))
    },
    {
      field: 'Cylinders',
      align: 'right',
      headerName: t('vehicle.cylinders'),
      minWidth: 50,
      flex: 1,
      disableColumnMenu: true,
      filterable: !!cylinders.options.length,
      valueOptions: cylinders.options,
      type: 'singleSelect',
      filterOperators: getGridSingleSelectOperators()
        .filter(op => op.value === 'is')
        .map(operator => ({
          ...operator,
          InputComponent: GridHeaderFilterSingleSelect,
          InputComponentProps: { options: cylinders.options }
        }))
    }
  ]

  const isEmptyOrUnknown = (value: any) => {
    return !value || value === 'unknown'
  }

  // remove columns where all data is empty (depending on data provider)
  const filteredColumns = columns.filter(
    column =>
      !!filteredTableData.find(value =>
        column.valueGetter
          ? !isEmptyOrUnknown(column.valueGetter({ row: value } as GridValueGetterParams<IVehicleType>))
          : !isEmptyOrUnknown(value[column.field as keyof IVehicleType])
      )
  )

  return (
    <Stack spacing={2} data-testid='vehicle-typeselection'>
      <DataGridPro
        apiRef={apiRef}
        autoHeight
        disableColumnFilter
        unstable_headerFilters
        disableMultipleRowSelection
        density='compact'
        localeText={localeText}
        columns={filteredColumns}
        rows={filteredTableData}
        pagination={false}
        hideFooterSelectedRowCount
        disableVirtualization // unmounts rows on testing, not necesary for performance on this specific grid
        getRowId={(row: IVehicleType) => [...Object.values(row?.GenericVehicle).filter(i => !!i)].toString()}
        slots={{
          headerFilterMenu: null,
          toolbar: DataGridToolbarWithSearch
        }}
        slotProps={{
          toolbar: { name: 'VehicleTypeSelectorSearchInput', placeholder: t('Vehicle.SearchType'), setFilter, filter }
        }}
        onRowClick={(params: GridRowParams<IVehicleType>) => {
          onTypeSelect(params.row)
        }}
        sx={gridHeaderStyling}
      />
    </Stack>
  )
}
